import { useState, useRef, useMemo, useContext } from 'react';
import classNames from 'classnames';
import { CSSTransition } from 'react-transition-group';
import {
  useAttachedImageTooltipState,
  useBreakpoint,
  useComparingConversationsParams,
  useConversationParams,
  useLoadDefaultMessageList,
  useSession,
} from 'src/hooks';
import { RandomPillars } from 'src/pages/ManageTasksChatPage/components/SkillPillars';
import { ThreadInputBox } from 'src/v2/components/ThreadInputBox';
import { GreetingTitle } from 'src/pages/ManageTasksChatPage/components/GreetingTitle';
import { ANIMATION_TIMEOUT } from 'src/constants';
import { useRightSidePanelState, useIsOpenModalImagesList } from 'src/hooks';
import { CompareModeView } from 'src/v2/components/CompareModeView';
import { MessageListContainer } from 'src/v2/components/MessageListContainer';
import ForwardRefContext from 'src/contexts/ForwardRefContext';
import { useChatHistory } from 'src/hooks';
import defaultImage from 'src/images/backgrounds/goodByeMyNinja.png';
import { useLocation } from 'react-router-dom';
import { AppRoutes } from 'src/types';

interface ThreadViewProps {
  startSpeechRecognizing: (defaultValue?: string) => Promise<void>;
  stopSpeechRecognizing: () => Promise<void>;
}

export const ThreadView = ({
  startSpeechRecognizing,
  stopSpeechRecognizing,
}: ThreadViewProps) => {
  const { pathname } = useLocation();
  const { isNinjaRetired } = useChatHistory();

  useLoadDefaultMessageList();

  const { isLandingPage } = useConversationParams();
  const { isImageEditSectionExpanded } = useRightSidePanelState();
  const { isOpenModalImagesList } = useIsOpenModalImagesList();
  const { isAttachedImageEditingMode } = useAttachedImageTooltipState();
  const { isEnterpriseTier } = useSession();
  const { isMobileOrTablet } = useBreakpoint();
  const { isCompareMode } = useComparingConversationsParams();

  const { mainMessageListRef } = useContext(ForwardRefContext);

  const inputBoxRef = useRef<HTMLDivElement>(null);

  const [animationInProgress, setAnimationInProgress] = useState(false);

  const isChatHistory = pathname === AppRoutes.CHAT_HISTORY;

  const disclaimer = useMemo(() => {
    if (isEnterpriseTier) {
      return 'Please do not post or share any sensitive company information';
    }
    return 'Ninja can make mistakes. Please double check responses.';
  }, [isEnterpriseTier]);

  const handleAnimationStart = () => {
    setAnimationInProgress(true);
  };

  const handleAnimationEnd = () => {
    setAnimationInProgress(false);
  };

  const isNotCompareMode =
    isLandingPage && !isAttachedImageEditingMode && !isCompareMode;

  return (
    <div className="nj-thread-view">
      <div
        className={classNames('nj-thread-view--chat', {
          'is-landing-page': isLandingPage,
          'is-landing-page--display-none': isLandingPage && !isCompareMode,
        })}
      >
        {isCompareMode ? (
          <CompareModeView />
        ) : (
          <MessageListContainer messageListRef={mainMessageListRef} />
        )}
      </div>

      {isNinjaRetired && !isCompareMode && isLandingPage && isChatHistory && (
        <div className="nj-thread-view--ninja-retired">
          <div className="nj-thread-view--ninja-retired-content">
            <img src={defaultImage} alt="Ninja is retired" />
            <p>Select a conversation thread to view task history</p>
          </div>
        </div>
      )}

      {!(isNinjaRetired && !isLandingPage) && !isChatHistory && (
        <CSSTransition
          in={!isLandingPage || isAttachedImageEditingMode}
          timeout={ANIMATION_TIMEOUT}
          classNames="nj-animate-input-box-transition"
          onEnter={handleAnimationStart}
          onEntered={handleAnimationEnd}
          nodeRef={inputBoxRef}
        >
          <div
            className={classNames('nj-thread-view--landing-page-wrapper', {
              'align-bottom':
                (!isLandingPage && !animationInProgress) || isCompareMode,
              'z-index-high':
                isImageEditSectionExpanded && !isOpenModalImagesList,
              'align-center': isNotCompareMode,
            })}
            ref={inputBoxRef}
          >
            {isNotCompareMode && <GreetingTitle />}

            <ThreadInputBox
              startSpeechRecognizing={startSpeechRecognizing}
              stopSpeechRecognizing={stopSpeechRecognizing}
            />

            {isNotCompareMode && <RandomPillars />}
          </div>
        </CSSTransition>
      )}

      {!isMobileOrTablet &&
        !(isNinjaRetired && !isLandingPage) &&
        !isChatHistory && (
          <p className="nj-thread-view--description">{disclaimer}</p>
        )}
    </div>
  );
};
